import React, { useEffect, useState, useMemo } from "react";
import {
  TextField,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  useTheme,
  Typography,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "api/userAxios";
import { City, Country } from "types/Country";
import {
  genderOptions,
  militaryServiceOptions,
  requestedResumeLanguageOptions,
  photoIncludedOptions,
} from "./mockData";

import { Resume } from "types/resumes-managment/resume";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "components/Loader";
import {
  EyeOutlined,
  SearchOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  ColumnDef,
  HeaderGroup,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from "@tanstack/react-table";
import IconButton from "components/@extended/IconButton";
import { MouseEvent } from "react";
import MainCard from "components/MainCard";
import { useCountriesForAutocomplete } from "hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "hooks/query-hooks/useCities";
import { useJobActivitiesForAutocomplete } from "hooks/query-hooks/useJobActivities";
import { useJobTypesForAutocomplete } from "hooks/query-hooks/useJobTypes";
import { useLanguagesForAutocomplete } from "hooks/query-hooks/useLanguages";
import { useDebounce } from "hooks";
import { CompanyActivity } from "types/companies-managment/companyActivity";
import { JobType } from "types/site-params/jobType";
import { Language } from "types/site-params/language";

const SearchResumes = () => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedJobActivity, setSelectedJobActivity] =
    useState<CompanyActivity | null>(null);
  const [selectedJobType, setSelectedJobType] = useState<JobType | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );

  const [requestedResumeLanguage, setRequestedResumeLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [militaryService, setMilitaryService] = useState("");
  const [photoIncluded, setPhotoIncluded] = useState("");
  const [status, setStatus] = useState("");
  const [searchResults, setSearchResults] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [resumeDeleteId, setResumeDeleteId] = useState<any>("");

  // Search states for autocomplete
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [jobActivitySearchTerm, setJobActivitySearchTerm] = useState("");
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
  const [languageSearchTerm, setLanguageSearchTerm] = useState("");

  // Debounced search terms
  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedJobActivitySearchTerm = useDebounce(
    jobActivitySearchTerm,
    500
  );
  const debouncedJobTypeSearchTerm = useDebounce(jobTypeSearchTerm, 500);
  const debouncedLanguageSearchTerm = useDebounce(languageSearchTerm, 500);

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(
    selectedCountry?.id || null,
    20,
    debouncedCitySearchTerm
  );

  const {
    items: jobActivities,
    loading: loadingJobActivities,
    hasMore: hasMoreJobActivities,
    loadMore: loadMoreJobActivities,
    error: jobActivitiesError,
  } = useJobActivitiesForAutocomplete(20, debouncedJobActivitySearchTerm);

  const {
    items: jobTypes,
    loading: loadingJobTypes,
    hasMore: hasMoreJobTypes,
    loadMore: loadMoreJobTypes,
    error: jobTypesError,
  } = useJobTypesForAutocomplete(20, debouncedJobTypeSearchTerm);

  const {
    items: languages,
    loading: loadingLanguages,
    hasMore: hasMoreLanguages,
    loadMore: loadMoreLanguages,
    error: languagesError,
  } = useLanguagesForAutocomplete(20, debouncedLanguageSearchTerm);

  const handleClose = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.toString()) {
      setSearchText(params.get("id") || "");
      setSelectedCountry(
        countries.find((c) => c.id.toString() === params.get("country_id")) ||
          null
      );
      setRequestedResumeLanguage(params.get("requested_resume_language") || "");
      setGender(params.get("gender") || "");
      setMilitaryService(params.get("military_service") || "");
      setPhotoIncluded(params.get("photo_included") || "");
      setStatus(params.get("status") || "");
      setHasSearched(true);
      fetchData();
    } else {
      setHasSearched(false);
      setSearchResults([]);
    }
  }, [location.search, countries, jobActivities, jobTypes, languages]);

  useEffect(() => {
    setSelectedCity(null);
    if (selectedCountry) {
      const params = new URLSearchParams(location.search);
      const cityId = params.get("city_id");
      if (cityId) {
        const city = cities.find((c: City) => c.id.toString() === cityId);
        if (city) {
          setSelectedCity(city);
        }
      }
    }
  }, [selectedCountry, cities]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobActivityId = params.get("desired_work_department");
    if (jobActivityId && jobActivities.length > 0) {
      const jobActivity = jobActivities.find(
        (ja) => ja.id.toString() === jobActivityId
      );
      if (jobActivity) {
        setSelectedJobActivity(jobActivity);
      }
    }
  }, [jobActivities]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jobTypeId = params.get("targeted_job_type");
    if (jobTypeId && jobTypes.length > 0) {
      const jobType = jobTypes.find((jt) => jt.id.toString() === jobTypeId);
      if (jobType) {
        setSelectedJobType(jobType);
      }
    }
  }, [jobTypes]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const languageId = params.get("foreign_languages");
    if (languageId && languages.length > 0) {
      const language = languages.find((l) => l.id.toString() === languageId);
      if (language) {
        setSelectedLanguage(language);
      }
    }
  }, [languages]);

  const fetchData = () => {
    setLoading(true);
    USERAXIOS.get(`/resumes${location.search}`)
      .then((response) => {
        setSearchResults(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching resumes:", error);
        setLoading(false);
      });
  };

  const handleSearch = () => {
    setHasSearched(true);
    const filters: any = {
      requested_resume_language: requestedResumeLanguage,
      gender,
      military_service: militaryService,
      photo_included: photoIncluded,
      status,
      country_id: selectedCountry ? selectedCountry.id.toString() : "",
      city_id: selectedCity ? selectedCity.id.toString() : "",
      desired_work_department: selectedJobActivity
        ? selectedJobActivity.id.toString()
        : "",
      targeted_job_type: selectedJobType ? selectedJobType.id.toString() : "",
      foreign_languages: selectedLanguage ? selectedLanguage.id.toString() : "",
    };

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        queryParams.set(key, value);
      }
    });

    if (searchText) {
      queryParams.set("id", searchText);
    }

    navigate({ search: queryParams.toString() });
  };

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSelectedCity(null);
    setSelectedJobActivity(null);
    setSelectedJobType(null);
    setSelectedLanguage(null);
    setRequestedResumeLanguage("");
    setGender("");
    setMilitaryService("");
    setPhotoIncluded("");
    setStatus("");
    setSearchResults([]);
    setSearchText("");
    setHasSearched(false);
    // Clear search terms
    setCountrySearchTerm("");
    setCitySearchTerm("");
    setJobActivitySearchTerm("");
    setJobTypeSearchTerm("");
    setLanguageSearchTerm("");
    navigate({ search: "" });
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo<ColumnDef<Resume>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "user_name",
      },
      {
        header: () => <FormattedMessage id="Email" />,
        accessorKey: "email",
      },
      {
        header: () => <FormattedMessage id="Language" />,
        accessorKey: "lang",
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        cell: ({ row }) => (
          <span style={{ color: row.original.status ? "green" : "red" }}>
            {row.original.status ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: () => <FormattedMessage id="Created At" />,
        accessorKey: "created_at",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const { id } = row.original;
          const handleEditResume = () => {
            window.location.href = `resumes-list/edit-resume/${id}`;
          };
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="View Details">
                <IconButton
                  color="primary"
                  href={`/resumes-list/view-resume-details/${id}`}
                  target="_blank"
                >
                  <EyeOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Resume">
                <IconButton color="primary" onClick={handleEditResume}>
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setResumeDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme]
  );

  const table = useReactTable({
    data: searchResults,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  return (
    <MainCard title={<FormattedMessage id="Search Resumes" />}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by resume id..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={selectedCountry}
            onChange={(event, newValue) => {
              setSelectedCountry(newValue);
              setSelectedCity(null);
              // Clear search term when option is selected
              if (newValue) {
                setCountrySearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setCountrySearchTerm(newInputValue);
              }
            }}
            options={countries}
            getOptionLabel={(option: Country) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="Desired Work Location" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCountries && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreCountries &&
                  !loadingCountries
                ) {
                  loadMoreCountries();
                }
              },
            }}
            loading={loadingCountries}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedCountrySearchTerm.length >= 2
                ? "No countries found"
                : "Type to search countries"
            }
          />
          {countriesError && (
            <FormHelperText error>
              Error loading countries. Please try again.
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={selectedCity}
            onChange={(event, newValue) => {
              setSelectedCity(newValue);
              // Clear search term when option is selected
              if (newValue) {
                setCitySearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setCitySearchTerm(newInputValue);
              }
            }}
            options={cities}
            getOptionLabel={(option: City) => option.name}
            disabled={!selectedCountry}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="City" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCities && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreCities &&
                  !loadingCities
                ) {
                  loadMoreCities();
                }
              },
            }}
            loading={loadingCities}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedCitySearchTerm.length >= 2
                ? "No cities found"
                : "Type to search cities"
            }
          />
          {citiesError && (
            <FormHelperText error>
              Error loading cities. Please try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            value={selectedJobActivity}
            onChange={(event, newValue) => {
              setSelectedJobActivity(newValue);
              // Clear search term when option is selected
              if (newValue) {
                setJobActivitySearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setJobActivitySearchTerm(newInputValue);
              }
            }}
            options={jobActivities}
            getOptionLabel={(option: CompanyActivity) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="Desired Work Department" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingJobActivities && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreJobActivities &&
                  !loadingJobActivities
                ) {
                  loadMoreJobActivities();
                }
              },
            }}
            loading={loadingJobActivities}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedJobActivitySearchTerm.length >= 2
                ? "No job activities found"
                : "Type to search job activities"
            }
          />
          {jobActivitiesError && (
            <FormHelperText error>
              Error loading job activities. Please try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            value={selectedJobType}
            onChange={(event, newValue) => {
              setSelectedJobType(newValue);
              // Clear search term when option is selected
              if (newValue) {
                setJobTypeSearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setJobTypeSearchTerm(newInputValue);
              }
            }}
            options={jobTypes}
            getOptionLabel={(option: JobType) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="Targeted Job Type" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingJobTypes && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreJobTypes &&
                  !loadingJobTypes
                ) {
                  loadMoreJobTypes();
                }
              },
            }}
            loading={loadingJobTypes}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedJobTypeSearchTerm.length >= 2
                ? "No job types found"
                : "Type to search job types"
            }
          />
          {jobTypesError && (
            <FormHelperText error>
              Error loading job types. Please try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            value={selectedLanguage}
            onChange={(event, newValue) => {
              setSelectedLanguage(newValue);
              // Clear search term when option is selected
              if (newValue) {
                setLanguageSearchTerm("");
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setLanguageSearchTerm(newInputValue);
              }
            }}
            options={languages}
            getOptionLabel={(option: Language) =>
              theme.direction === "rtl" ? option.name_trans.ar : option.name
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="Foreign Languages" />}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLanguages && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                const threshold = 100;
                if (
                  listboxNode.scrollHeight - listboxNode.scrollTop <=
                    listboxNode.clientHeight + threshold &&
                  hasMoreLanguages &&
                  !loadingLanguages
                ) {
                  loadMoreLanguages();
                }
              },
            }}
            loading={loadingLanguages}
            filterOptions={(x) => x} // Disable built-in filtering
            noOptionsText={
              debouncedLanguageSearchTerm.length >= 2
                ? "No languages found"
                : "Type to search languages"
            }
          />
          {languagesError && (
            <FormHelperText error>
              Error loading languages. Please try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Requested Resume Language" />
            </InputLabel>
            <Select
              value={requestedResumeLanguage}
              onChange={(e) => setRequestedResumeLanguage(e.target.value)}
              label={<FormattedMessage id="Requested Resume Language" />}
            >
              <MenuItem value="">
                <FormattedMessage id="Default" />
              </MenuItem>
              {requestedResumeLanguageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Gender" />
            </InputLabel>
            <Select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              label={<FormattedMessage id="Gender" />}
            >
              <MenuItem value="">
                <FormattedMessage id="Default" />
              </MenuItem>
              {genderOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Military Service" />
            </InputLabel>
            <Select
              value={militaryService}
              onChange={(e) => setMilitaryService(e.target.value)}
              label={<FormattedMessage id="Military Service" />}
            >
              <MenuItem value="">
                <FormattedMessage id="Default" />
              </MenuItem>
              {militaryServiceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Photo Included" />
            </InputLabel>
            <Select
              value={photoIncluded}
              onChange={(e) => setPhotoIncluded(e.target.value)}
              label={<FormattedMessage id="Photo Included" />}
            >
              <MenuItem value="">
                <FormattedMessage id="Default" />
              </MenuItem>
              {photoIncludedOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {theme.direction === "rtl"
                    ? option.arabic_label
                    : option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="Status" />
            </InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label={<FormattedMessage id="Status" />}
            >
              <MenuItem value="">
                <FormattedMessage id="Default" />
              </MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClearFilters}
          sx={{ width: "200px" }}
        >
          <FormattedMessage id="Clear Filters" />
        </Button>
        <Button
          onClick={handleSearch}
          variant="contained"
          color="primary"
          sx={{ width: "200px" }}
        >
          <FormattedMessage id="Search" />
        </Button>
      </Box>

      {loading ? (
        <Loader />
      ) : hasSearched ? (
        searchResults.length > 0 ? (
          <TableContainer sx={{ mt: 5, boxShadow: "none" }}>
            <Table>
              <TableHead>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Stack
            sx={{ mt: 5, textAlign: "center" }}
            spacing={1}
            alignItems="center"
          >
            <InboxOutlined
              style={{ fontSize: "5rem", color: theme.palette.grey[400] }}
            />
            <Typography variant="h6">
              <FormattedMessage id="No available data" />
            </Typography>
          </Stack>
        )
      ) : null}
    </MainCard>
  );
};

export default SearchResumes;
